// Funil próprio do site público (POST /api/v1/site-events). Sem cookie e sem
// dado pessoal: a sessão é um código aleatório no sessionStorage da aba, por
// isso independe do consentimento das tags de anúncio. O mesmo código vai no
// fim da mensagem do WhatsApp ("Cód. G-7K3F2") para cruzar conversa e sessão.

import { getApiBaseUrl } from './apiBase';

export type TrafficSource = 'google_ads' | 'meta_ads' | 'organic_search' | 'referral' | 'direct';
export type SiteEventType = 'landing' | 'page_view' | 'property_view' | 'whatsapp_click' | 'lead';

export interface Attribution {
  source: TrafficSource;
  gclid?: string;
  gad_campaignid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

interface EventOptions {
  path?: string;
  whatsappSource?: string;
}

export const SESSION_STORAGE_KEY = 'site_session_v1';
export const ATTRIBUTION_STORAGE_KEY = 'site_attribution_v1';
export const LANDING_STORAGE_KEY = 'site_landing_v1';
export const AD_CLICK_STORAGE_KEY = 'site_ad_click_v1';

// Sem 0/O/1/I para a corretora conseguir ler o código em voz alta.
export const CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const SESSION_LENGTH = 12;
const CODE_LENGTH = 5;
const UNTRACKED_PREFIXES = ['/admin', '/superadmin', '/login'];
const SEARCH_ENGINES = /(^|\.)(google|bing|yahoo|duckduckgo|yandex|ecosia|baidu|brave|qwant)\./i;
const META_UTM = /^(facebook|instagram|fb|ig|meta)$/i;
const AD_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', 'gad_source', 'fbclid'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SOURCE_LETTER: Record<TrafficSource, string> = {
  google_ads: 'G',
  meta_ads: 'M',
  organic_search: 'S',
  referral: 'D',
  direct: 'D',
};

// Espelho em memória para quando o sessionStorage está bloqueado.
const memory = new Map<string, string>();
let storageBroken = false;

const read = (key: string): string | null => {
  try {
    const value = window.sessionStorage.getItem(key);
    if (value !== null || !storageBroken) return value;
  } catch {
    storageBroken = true;
  }
  return memory.get(key) ?? null;
};

const write = (key: string, value: string) => {
  memory.set(key, value);
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    storageBroken = true;
  }
};

const randomBytes = (size: number): Uint8Array => {
  const bytes = new Uint8Array(size);
  if (window.crypto?.getRandomValues) return window.crypto.getRandomValues(bytes);
  return bytes.map(() => Math.floor(Math.random() * 256));
};

export const generateSessionId = (random: (size: number) => Uint8Array = randomBytes): string =>
  Array.from(random(SESSION_LENGTH))
    .slice(0, SESSION_LENGTH)
    .map((byte) => CODE_ALPHABET[byte % CODE_ALPHABET.length])
    .join('');

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const classifySource = (
  params: URLSearchParams,
  referrer: string,
  ownHost: string
): TrafficSource => {
  if (['gclid', 'gbraid', 'wbraid', 'gad_source'].some((key) => params.get(key))) return 'google_ads';
  if (params.get('fbclid') || META_UTM.test(params.get('utm_source') || '')) return 'meta_ads';
  const host = hostOf(referrer);
  if (!host) return 'direct';
  if (SEARCH_ENGINES.test(host)) return 'organic_search';
  return host !== ownHost.toLowerCase().replace(/^www\./, '') ? 'referral' : 'direct';
};

const optional = (params: URLSearchParams, key: string, limit: number) => {
  const value = params.get(key)?.trim();
  return value ? { [key]: value.slice(0, limit) } : {};
};

const buildAttribution = (url: URL, referrer: string): Attribution => {
  const params = url.searchParams;
  return {
    source: classifySource(params, referrer, url.hostname),
    ...optional(params, 'gclid', 200),
    ...optional(params, 'gad_campaignid', 40),
    ...optional(params, 'utm_source', 100),
    ...optional(params, 'utm_medium', 100),
    ...optional(params, 'utm_campaign', 100),
  };
};

const adClickOf = (params: URLSearchParams): string =>
  AD_CLICK_PARAMS.map((key) => params.get(key) || '').join('|').replace(/^\|+$/, '');

const openSession = (attribution: Attribution, adClick: string) => {
  write(SESSION_STORAGE_KEY, generateSessionId());
  write(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  write(AD_CLICK_STORAGE_KEY, adClick);
  write(LANDING_STORAGE_KEY, '');
};

/** Chamado na carga da página: abre a sessão ou, num novo clique de anúncio, outra. */
export const startSiteSession = (
  href: string = window.location.href,
  referrer: string = document.referrer
) => {
  try {
    const url = new URL(href);
    const adClick = adClickOf(url.searchParams);
    const known = read(SESSION_STORAGE_KEY) && read(ATTRIBUTION_STORAGE_KEY);
    if (!known || (adClick && adClick !== read(AD_CLICK_STORAGE_KEY))) {
      openSession(buildAttribution(url, referrer), adClick);
    }
  } catch {
    // Atribuição é acessória: nunca quebra a página.
  }
};

export const getSessionId = (): string => {
  if (!read(SESSION_STORAGE_KEY)) startSiteSession();
  let id = read(SESSION_STORAGE_KEY);
  if (!id) {
    id = generateSessionId();
    write(SESSION_STORAGE_KEY, id);
  }
  return id;
};

export const getAttribution = (): Attribution => {
  if (!read(ATTRIBUTION_STORAGE_KEY)) startSiteSession();
  try {
    const stored = JSON.parse(read(ATTRIBUTION_STORAGE_KEY) || '');
    if (stored && typeof stored.source === 'string') return stored as Attribution;
  } catch {
    // Valor corrompido: trata como acesso direto.
  }
  return { source: 'direct' };
};

export const originCode = (): string =>
  `${SOURCE_LETTER[getAttribution().source] || 'D'}-${getSessionId().slice(0, CODE_LENGTH).toUpperCase()}`;

/** Acrescenta "Cód. G-7K3F2" numa linha própria no fim da mensagem do WhatsApp. */
export const withOriginCode = (message: string): string => {
  const code = `Cód. ${originCode()}`;
  const text = (message || '').trimEnd();
  if (text.endsWith(code)) return text;
  return text ? `${text}\n\n${code}` : code;
};

export const isTrackedSitePath = (pathname: string) =>
  !UNTRACKED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const pageContext = (path: string) => {
  const [, kind, slug] = path.split('/');
  if ((kind === 'imovel' || kind === 'properties') && UUID.test(slug || '')) {
    return { property_id: slug.toLowerCase() };
  }
  if ((kind === 'condominio' || kind === 'bairro') && /^[a-z0-9-]{1,120}$/.test(slug || '')) {
    return { location_slug: slug };
  }
  return {};
};

const transmit = (body: string) => {
  const url = `${getApiBaseUrl()}/api/v1/site-events`;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
  } catch {
    // Sem beacon: tenta o fetch abaixo.
  }
  try {
    window
      .fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      })
      .catch(() => undefined);
  } catch {
    // Envio é melhor esforço.
  }
};

export const sendSiteEvent = (type: SiteEventType, options: EventOptions = {}) => {
  try {
    const path = (options.path || window.location.pathname).split(/[?#]/)[0].slice(0, 300) || '/';
    if (!isTrackedSitePath(path)) return;
    const payload = {
      session_id: getSessionId(),
      event_type: type,
      path,
      ...pageContext(path),
      ...getAttribution(),
      ...(options.whatsappSource ? { whatsapp_source: options.whatsappSource } : {}),
    };
    transmit(JSON.stringify(payload));
  } catch {
    // Medição nunca quebra a página.
  }
};

let lastNavigation: string | null = null;

/** Estado de módulo some a cada carga de página; os testes simulam isso aqui. */
export const resetSiteEventsState = () => {
  lastNavigation = null;
  storageBroken = false;
  memory.clear();
};

/** Entrada da sessão (uma vez) e page_view a cada rota pública. */
export const trackSiteNavigation = (pathname: string) => {
  if (!isTrackedSitePath(pathname) || pathname === lastNavigation) return;
  lastNavigation = pathname;
  if (!read(LANDING_STORAGE_KEY)) {
    write(LANDING_STORAGE_KEY, pathname);
    sendSiteEvent('landing', { path: pathname });
  }
  sendSiteEvent('page_view', { path: pathname });
};
